// The request workspace (D98): one page per request with stage tabs and a single next step.
// Pure functions so the screens and the tests agree; the broker still decides every action.

import type { PrStatus } from '../rules/statuses';

/** What Procurement, the CFO and Finance follow. */
export const STAFF_STAGES = ['review', 'quotes', 'po', 'delivery', 'invoice', 'payment'] as const;
/** What the requester and their department head follow: no PO, invoice or payment (D64). */
export const REQUESTER_STAGES = ['review', 'buying', 'delivery'] as const;

export type Stage = (typeof STAFF_STAGES)[number] | (typeof REQUESTER_STAGES)[number];
export type StageState = 'done' | 'current' | 'todo' | 'attention' | 'hold' | 'stopped' | 'skipped';

export interface StageView {
  key: Stage;
  state: StageState;
  /** Route of the stage's page for this viewer, or null when there is nothing to open. */
  href: string | null;
}

/** Stages done, and the state of the stage after them. */
const STAFF: Record<PrStatus, [number, StageState | null]> = {
  Draft: [0, 'attention'],
  Returned: [0, 'attention'],
  ProcurementReview: [0, 'current'],
  WithDeptHead: [0, 'current'],
  Rejected: [0, 'stopped'],
  Cancelled: [0, 'stopped'],
  Sourcing: [1, 'current'],
  OnHold: [1, 'hold'],
  PettyCash: [1, 'current'],
  PODraft: [2, 'current'],
  PendingCFO: [2, 'current'],
  PendingVertical: [2, 'current'],
  POSentPendingCFO: [3, 'current'],
  POIssued: [3, 'current'],
  PartlyReceived: [3, 'current'],
  Received: [4, 'current'],
  Invoiced: [5, 'current'],
  VoucherWithFinance: [5, 'current'],
  Paid: [5, 'current'],
  Closed: [6, null],
};

const REQUESTER: Record<PrStatus, [number, StageState | null]> = {
  Draft: [0, 'attention'],
  Returned: [0, 'attention'],
  ProcurementReview: [0, 'current'],
  WithDeptHead: [0, 'current'],
  Rejected: [0, 'stopped'],
  Cancelled: [0, 'stopped'],
  Sourcing: [1, 'current'],
  OnHold: [1, 'hold'],
  PettyCash: [1, 'current'],
  PODraft: [1, 'current'],
  PendingCFO: [1, 'current'],
  PendingVertical: [1, 'current'],
  POSentPendingCFO: [2, 'current'],
  POIssued: [2, 'current'],
  PartlyReceived: [2, 'current'],
  Received: [3, null],
  Invoiced: [3, null],
  VoucherWithFinance: [3, null],
  Paid: [3, null],
  Closed: [3, null],
};

export interface Viewer {
  /** Procurement, CFO and Finance get the full stages (the broker's staffView). */
  staff: boolean;
  procurement: boolean;
  cfo: boolean;
}

const STAGE_PATH: Partial<Record<Stage, string>> = {
  quotes: '/quotes',
  po: '/po',
  delivery: '/delivery',
  invoice: '/invoice',
  payment: '/payments',
};

/** The stage tabs a viewer sees, each linked to its page when the viewer can open it. */
export function stagesFor(status: PrStatus, viewer: Viewer, hasPo: boolean): StageView[] {
  const keys: readonly Stage[] = viewer.staff ? STAFF_STAGES : REQUESTER_STAGES;
  const [done, next] = (viewer.staff ? STAFF : REQUESTER)[status];
  // Petty cash buys without an RFQ, PO or supplier invoice (D65).
  const skipped = viewer.staff && status === 'PettyCash' ? new Set<Stage>(['po', 'invoice']) : null;
  return keys.map((key, i) => {
    let state: StageState = 'todo';
    if (skipped?.has(key)) state = 'skipped';
    else if (i < done) state = 'done';
    else if (i === done && next) state = next;
    return { key, state, href: hrefFor(key, state, viewer, hasPo) };
  });
}

function hrefFor(key: Stage, state: StageState, viewer: Viewer, hasPo: boolean): string | null {
  if (key === 'review') return '';
  if (state === 'todo' || state === 'skipped') return null;
  // The requester (and Procurement on their behalf) confirm deliveries once a PO is out.
  if (key === 'delivery') return hasPo ? '/delivery' : null;
  if (!viewer.procurement) return null;
  if (key === 'payment' && !hasPo) return null;
  return STAGE_PATH[key] ?? null;
}

/** The stage a status sits in, for the tab shown as selected on the request page. */
export function currentStage(status: PrStatus, staff: boolean): Stage {
  const keys: readonly Stage[] = staff ? STAFF_STAGES : REQUESTER_STAGES;
  const [done] = (staff ? STAFF : REQUESTER)[status];
  return keys[Math.min(done, keys.length - 1)] ?? 'review';
}

// ---- Who has it, and the one next step ----

export type Owner =
  | 'requester'
  | 'procurement'
  | 'deptHead'
  | 'cfo'
  | 'verticalApprover'
  | 'finance'
  | 'supplier'
  | 'none';

const OWNER: Record<PrStatus, Owner> = {
  Draft: 'requester',
  Returned: 'requester',
  ProcurementReview: 'procurement',
  WithDeptHead: 'deptHead',
  Sourcing: 'procurement',
  OnHold: 'procurement',
  PettyCash: 'procurement',
  PODraft: 'procurement',
  PendingCFO: 'cfo',
  PendingVertical: 'verticalApprover',
  POSentPendingCFO: 'supplier',
  POIssued: 'supplier',
  PartlyReceived: 'supplier',
  Received: 'procurement',
  Invoiced: 'procurement',
  VoucherWithFinance: 'finance',
  Paid: 'procurement',
  Closed: 'none',
  Rejected: 'none',
  Cancelled: 'none',
};

export function ownerOf(status: PrStatus): Owner {
  return OWNER[status];
}

/** Things the request page can offer; the screen maps each to a link or a form. */
export type ActionId =
  | 'edit'
  | 'headDecide'
  | 'review'
  | 'quotes'
  | 'rfq'
  | 'po'
  | 'cfo'
  | 'delivery'
  | 'deliveryView'
  | 'invoice'
  | 'payments'
  | 'poDoc';

export interface NextStepInput {
  status: PrStatus;
  viewer: Viewer;
  canEdit: boolean;
  canConfirmDelivery: boolean;
  canRecordInvoice: boolean;
  hasPo: boolean;
  poNo: string;
  /** The viewer may take a department-head decision now. */
  headDecides: boolean;
}

export interface NextStep {
  /** Whose move it is. */
  owner: Owner;
  /** The one main button, or null when the viewer has nothing to do now. */
  primary: ActionId | null;
  /** Other ways in, shown as plain buttons. Never repeats the primary. */
  secondary: ActionId[];
}

const POST_PO: readonly PrStatus[] = [
  'POSentPendingCFO',
  'POIssued',
  'PartlyReceived',
  'Received',
  'Invoiced',
  'VoucherWithFinance',
  'Paid',
  'Closed',
];

/** The single next step for this viewer (design pass, artboard 1). */
export function nextStep(input: NextStepInput): NextStep {
  const { status, viewer } = input;
  // Delivered is the end for the requester (D100): what follows is Procurement's and Finance's.
  const owner: Owner =
    !viewer.staff && (status === 'Received' || status === 'Closed') ? 'none' : ownerOf(status);
  const out: ActionId[] = [];
  let primary: ActionId | null = null;
  const offer = (a: ActionId, main: boolean) => {
    if (main && primary === null) primary = a;
    else if (!out.includes(a)) out.push(a);
  };

  if (input.canEdit) offer('edit', true);
  if (input.headDecides) offer('headDecide', true);
  if (viewer.cfo && (status === 'PendingCFO' || status === 'POSentPendingCFO')) offer('cfo', true);

  if (viewer.procurement) {
    if (status === 'ProcurementReview') offer('review', true);
    if (status === 'Sourcing') {
      offer('quotes', true);
      offer('rfq', false);
      offer('po', false);
    }
    if (status === 'PODraft') offer('po', true);
    if (status === 'PendingCFO' || status === 'PendingVertical') offer('po', false);
    if (status === 'Received' && input.canRecordInvoice) offer('invoice', true);
    if (status === 'Invoiced' && input.canRecordInvoice) offer('payments', true);
  }

  if (input.hasPo) {
    if (input.canConfirmDelivery) offer('delivery', true);
    if (viewer.procurement && input.canRecordInvoice) {
      offer('invoice', false);
      offer('payments', false);
    }
    if (input.poNo && (viewer.procurement || viewer.cfo)) offer('poDoc', false);
    if (!input.canConfirmDelivery && POST_PO.includes(status)) offer('deliveryView', false);
  }

  return { owner, primary, secondary: out.filter((a) => a !== primary) };
}
